/* eslint-disable no-magic-numbers */
import { node } from "~/util/dom";

interface IScaleConf {
	min: number;
	max: number;
	padding: {
		left: number | string;
		right: number | string;
	};
}

export default class ControlScale {
	_conf: IScaleConf;

	_numOfParts: number;

	_scaleable: boolean;

	_partsList: Array<HTMLElement>;

	_numbersList: Array<HTMLElement>;

	_limits: {
		partMin: number;
		partMax: number;
		totalMax: number;
	};

	_units: {
		[key: string]: string;
	};

	_steps: Array<Array<number>>;

	_dom: {
		[key: string]: HTMLElement;
	};

	_visible: boolean;

	constructor(conf?: IScaleConf | number) {
		this._conf = {
			min: 1,
			max: 3,
			padding: {
				left: 0,
				right: "75%",
			},
		};

		// pocet dilku meritka
		this._numOfParts = 1;
		// dyn. menit pocet dilku
		this._scaleable = true;
		// pole s dom prvky
		this._partsList = [];
		this._numbersList = [];

		// nacteni konfigurace
		if (typeof conf === "number") {
			// min a max na stejne cislo
			this._conf.min = conf;
			this._conf.max = conf;
		} else if (typeof conf === "object") {
			Object.assign(this._conf, conf);
		}

		// osetreni
		this._conf.min = Math.max(Math.min(this._conf.min, this._conf.max), 1);
		this._conf.max = Math.max(Math.max(this._conf.min, this._conf.max), 1);

		// stejny pocet dilku min a max
		if (this._conf.min === this._conf.max) {
			this._scaleable = false;
			this._numOfParts = this._conf.min;
		}

		this._limits = {
			partMin: 50,
			partMax: 110,
			totalMax: 200,
		};

		this._units = {
			1: "m",
			1000: "km",
		};

		this._visible = true;

		this._steps = [ /* [pocet metru, zobrazovana jednotka] */
			[1.5, 1],
			[3, 1],
			[6, 1],
			[12, 1],
			[25, 1],
			[50, 1],
			[100, 1],
			[200, 1],
			[400, 1],
			[800, 1],
			[1500, 1000],
			[3000, 1000],
			[6000, 1000],
			[12000, 1000],
			[25000, 1000],
			[50000, 1000],
			[100000, 1000],
			[200000, 1000],
			[400000, 1000],
			[800000, 1000],
			[1500000, 1000],
			[3000000, 1000],
			[6000000, 1000],
			[10000000, 1000],
		];

		this._dom = {};
		this._dom.container = node("div", { className: "control-scale print" });
		this._dom.unit = null;

		// vytvorit dom a aktualizovat
		this._build();
	}

	get container() {
		return this._dom.container;
	}

	set visible(visible: boolean) {
		if (visible !== this._visible) {
			this._visible = visible;
			this._dom.container.style.display = this._visible ? "" : "none";
		}
	}

	_build() {
		const unit = node("span", { className: "unit" });
		const partsList: Array<HTMLElement> = [];
		const numbersList: Array<HTMLElement> = [];

		for (let ind = 0; ind < this._numOfParts + 2; ind++) {
			numbersList.push(node("span"));

			if (ind) {
				partsList.push(node("span", { className: ind % 2 ? "odd" : "even" }));
			}
		}

		const numbers = node("div", { className: "numbers" });

		numbersList.forEach(numbers.appendChild, numbers);

		const partsCover = node("div", { className: "parts-cover" });
		const unitHolder = node("div", { className: "unit-holder" });
		const parts = node("div", { className: "parts" });

		partsList.forEach(parts.appendChild, parts);
		partsCover.append(parts, unitHolder);
		unitHolder.append(unit);

		// dom s meritkem
		const frag = document.createDocumentFragment();

		frag.appendChild(numbers);
		frag.appendChild(partsCover);

		// clear & append
		this._dom.container.innerHTML = "";
		this._dom.container.appendChild(frag);

		this._partsList = partsList;
		this._numbersList = numbersList;
		this._dom.unit = unit;
	}

	setData(wgs84Y: number, zoom: number) {
		const scaleInfo = this._getScaleInfo(wgs84Y, zoom);

		if (!scaleInfo || !this._visible) {
			this._dom.container.style.display = "none";

			return;
		}

		// unit update
		this._dom.unit.innerHTML = this._units[scaleInfo.unit];

		let totalWidth = 0;
		let totalLength = 0;

		for (let ind = 0; ind <= this._numOfParts; ind++) {
			const number = this._numbersList[ind];

			number.style.display = "";
			/* o border */
			number.style.left = `${Math.round(1 + totalWidth)}px`;
			number.innerHTML = (totalLength / scaleInfo.unit).toString();

			/* dilek */
			if (ind < this._numOfParts) {
				const part = this._partsList[ind];

				part.style.display = "";
				part.style.width = `${Math.round(scaleInfo.width)}px`;
			}

			totalWidth += scaleInfo.width;
			totalLength += scaleInfo.step;
		}

		for (let ind = this._numOfParts + 1; ind < this._numbersList.length; ind++) {
			this._numbersList[ind].style.display = "none";
			this._partsList[ind - 1].style.display = "none";
		}

		this._dom.container.style.display = "";
	}

	_getScaleInfo(wgs84Y: number, zoom: number) {
		// lat Zeměpisná šířka
		const lat = wgs84Y;
		const equator = 156543.034 / Math.pow(2, zoom);
		const parallel = equator * Math.cos(lat * Math.PI / 180);
		const limits = this._limits;

		for (let ind = 0; ind < this._steps.length; ind++) {
			const def = this._steps[ind];
			const width = def[0] / parallel;

			if (width > limits.partMin && width < limits.partMax) {
				return {
					width: width,
					step: def[0],
					unit: def[1],
				};
			}
		}

		return null;
	}

	setCount(wgs84Y: number, zoom: number, widthArg: number) {
		if (!this._scaleable) {
			return;
		}

		const scaleInfo = this._getScaleInfo(wgs84Y, zoom);

		if (!scaleInfo) {
			return;
		}

		const width = widthArg;
		const padding = this._getPadding(width);
		const availWidth = width - padding.left - padding.right;
		const stepWidth = Math.ceil(scaleInfo.width);

		for (let ind = this._conf.max; ind >= this._conf.min; ind--) {
			if (ind * stepWidth <= availWidth) {
				const change = ind !== this._numOfParts;

				this._numOfParts = ind;

				if (change) {
					// zmena meritka - rebuild a update
					this._build();
					this.setData(wgs84Y, zoom);
				}

				break;
			}
		}
	}

	_getPadding(width: number) {
		const output = {
			left: 0,
			right: 0,
		};
		const padding = this._conf.padding;

		if (typeof padding.left === "number") {
			output.left = padding.left;
		} else if (typeof padding.left === "string" && padding.left.indexOf("%") !== -1) {
			output.left = Math.round(width * parseFloat(padding.left.replace("%", "")) / 100);
		}

		if (typeof padding.right === "number") {
			output.right = padding.right;
		} else if (typeof padding.right === "string" && padding.right.indexOf("%") !== -1) {
			output.right = Math.round(width * parseFloat(padding.right.replace("%", "")) / 100);
		}

		return output;
	}
}
